import axios from '@/utils/axios';

import type {
  CustomChatAttachment,
  CustomChatChannel,
  CustomChatMessage,
  CustomChatParticipantIdentity,
  CustomChatOpenChannelPayload,
  CustomChatAddParticipantsPayload,
  CustomChatSendGeneralMessagePayload,
  CustomChatSendMessagePayload,
  CustomChatTransferPayload,
} from '../types';

type ApiResponse<T> = {
  data: T;
  message?: string;
};

type MessagesResponse =
  | CustomChatMessage[]
  | {
      messages?: CustomChatMessage[];
      data?: CustomChatMessage[];
      next_cursor?: string | null;
    };

type UploadAttachmentResponse = {
  attachment?: CustomChatAttachment;
  attachments?: CustomChatAttachment[];
};

type AttachmentAccessUrlResponse = {
  expires_in: number;
  url: string;
};

const unwrapMessages = (value: MessagesResponse): CustomChatMessage[] => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value.messages)) return value.messages;
  if (Array.isArray(value.data)) return value.data;
  return [];
};

const openChannelRequests = new Map<string, Promise<CustomChatChannel>>();

const getOpenChannelRequestKey = (payload: CustomChatOpenChannelPayload) => {
  switch (payload.type) {
    case 'directMessage':
      return `${payload.type}:${payload.dashboard_user_ids
        .map(Number)
        .sort((firstId, secondId) => firstId - secondId)
        .join(',')}`;
    case 'clientSupport':
      return `${payload.type}:${payload.client_user_id}`;
    case 'cleanerSupport':
      return `${payload.type}:${payload.cleaner_user_id}`;
    case 'dashboardSupport':
      return `${payload.type}:${payload.dashboard_user_id}`;
    case 'jobSupport':
      return `${payload.type}:${payload.booking_session_id}`;
    default:
      return JSON.stringify(payload);
  }
};

export async function openCustomChatChannel(
  payload: CustomChatOpenChannelPayload
): Promise<CustomChatChannel> {
  const requestKey = getOpenChannelRequestKey(payload);
  const inFlightRequest = openChannelRequests.get(requestKey);

  if (inFlightRequest) return inFlightRequest;

  const request = axios
    .post<ApiResponse<CustomChatChannel>>('/chat/v2/channels/open', payload)
    .then(({ data: { data } }) => data)
    .finally(() => {
      openChannelRequests.delete(requestKey);
    });

  openChannelRequests.set(requestKey, request);

  return request;
}

export async function getCustomChatChannels(): Promise<CustomChatChannel[]> {
  const {
    data: { data },
  } = await axios.get<ApiResponse<CustomChatChannel[]>>('/chat/v2/channels');

  return data;
}

export async function getCustomChatMessages(
  channelId: number | string,
  params?: { after?: number | string; before?: number | string; limit?: number }
): Promise<CustomChatMessage[]> {
  const {
    data: { data },
  } = await axios.get<ApiResponse<MessagesResponse>>(
    `/chat/v2/channels/${channelId}/messages`,
    { params }
  );

  return unwrapMessages(data);
}

export async function sendCustomChatMessage(
  channelId: number | string,
  payload: CustomChatSendMessagePayload
): Promise<CustomChatMessage> {
  const {
    data: { data },
  } = await axios.post<ApiResponse<CustomChatMessage>>(
    `/chat/v2/channels/${channelId}/messages`,
    payload
  );

  return data;
}

export async function sendCustomChatGeneralMessage(
  channelId: number | string,
  payload: CustomChatSendGeneralMessagePayload
): Promise<CustomChatMessage> {
  const {
    data: { data },
  } = await axios.post<ApiResponse<CustomChatMessage>>(
    `/chat/v2/channels/${channelId}/general-messages`,
    payload
  );

  return data;
}

export async function uploadCustomChatAttachment(
  formData: FormData
): Promise<CustomChatAttachment> {
  const {
    data: { data },
  } = await axios.post<ApiResponse<UploadAttachmentResponse>>(
    '/chat/v2/attachments/upload',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  if (data.attachment) return data.attachment;
  if (data.attachments?.[0]) return data.attachments[0];

  throw new Error('Upload did not return an attachment');
}

export async function getCustomChatAttachmentAccessUrl(
  channelId: number | string,
  attachmentId: number | string
): Promise<AttachmentAccessUrlResponse> {
  const {
    data: { data },
  } = await axios.get<ApiResponse<AttachmentAccessUrlResponse>>(
    `/chat/v2/channels/${channelId}/attachments/${attachmentId}/access-url`
  );

  return data;
}

export async function markCustomChatChannelRead(
  channelId: number | string
): Promise<{ unread_count?: number }> {
  const {
    data: { data },
  } = await axios.patch<ApiResponse<{ unread_count?: number }>>(
    `/chat/v2/channels/${channelId}/read`,
    { read_at: new Date().toISOString() }
  );

  return data;
}

export async function pickUpCustomChatChannel(
  channelId: number | string
): Promise<CustomChatChannel> {
  const {
    data: { data },
  } = await axios.patch<ApiResponse<CustomChatChannel>>(
    `/chat/v2/channels/${channelId}/pick-up`
  );

  return data;
}

export async function transferCustomChatChannel(
  channelId: number | string,
  payload: CustomChatTransferPayload
): Promise<CustomChatChannel> {
  const {
    data: { data },
  } = await axios.patch<ApiResponse<CustomChatChannel>>(
    `/chat/v2/channels/${channelId}/transfer`,
    payload
  );

  return data;
}

export async function archiveCustomChatChannel(
  channelId: number | string,
  archived = true
): Promise<CustomChatChannel> {
  const {
    data: { data },
  } = await axios.patch<ApiResponse<CustomChatChannel>>(
    `/chat/v2/channels/${channelId}/archive`,
    { archived }
  );

  return data;
}

export async function setCustomChatExternalLock(
  channelId: number | string,
  locked: boolean
): Promise<CustomChatChannel> {
  const {
    data: { data },
  } = await axios.patch<ApiResponse<CustomChatChannel>>(
    `/chat/v2/channels/${channelId}/external-lock`,
    { locked }
  );

  return data;
}

export async function addCustomChatParticipants(
  channelId: number | string,
  payload: CustomChatAddParticipantsPayload
): Promise<CustomChatChannel> {
  const {
    data: { data },
  } = await axios.post<ApiResponse<CustomChatChannel>>(
    `/chat/v2/channels/${channelId}/participants`,
    payload
  );

  return data;
}

export async function removeCustomChatParticipant(
  channelId: number | string,
  participant: CustomChatParticipantIdentity
): Promise<CustomChatChannel> {
  const {
    data: { data },
  } = await axios.delete<ApiResponse<CustomChatChannel>>(
    `/chat/v2/channels/${channelId}/participants`,
    { data: participant }
  );

  return data;
}
